import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSessionStore } from "../store/sessionStore";
import { usePlaybackStore } from "../store/playbackStore";
import { useMixerStore } from "../store/mixerStore";
import { useSessionSocket } from "../ws/useSessionSocket";
import { apiClient } from "../api/client";
import { AudioEngine } from "../audio/AudioEngine";
import SessionTopBar from "../components/SessionTopBar";
import TransportBar from "../components/TransportBar";
import TimelineRuler from "../components/TimelineRuler";
import TracksPanel from "../components/TracksPanel";
import RecorderDock from "../components/RecorderDock";
import JoinRequestsWidget from "../components/JoinRequestsWidget";
import SessionUsersWidget from "../components/SessionUsersWidget";
import { buildAccessLoginUrl } from "../config";
import type { SessionUserSummary } from "@jamroom/shared";

interface SessionAccessState {
  checking: boolean;
  exists: boolean;
  title: string;
  requiresCode: boolean;
  requiresApproval: boolean;
  isPrivate: boolean;
  createdAt: string | null;
  expiresAt: string | null;
  serverNow: string | null;
  error: string | null;
}

const SessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [audioEngine, setAudioEngine] = useState<AudioEngine | null>(null);
  const loadedTrackIdsRef = useRef<Set<string>>(new Set());
  const rafIdRef = useRef<number | null>(null);
  const noticeTimeoutRef = useRef<number | null>(null);
  const [trackAudioErrors, setTrackAudioErrors] = useState<Record<string, string>>({});

  const [hasJoined, setHasJoined] = useState(false);
  const [joinName, setJoinName] = useState(localStorage.getItem("jamroom_display_name") || "");
  const [joinCode, setJoinCode] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinRequestId, setJoinRequestId] = useState<string | null>(null);
  const [joinRequestStatus, setJoinRequestStatus] = useState<"PENDING" | "DENIED" | null>(null);
  const [joinRequestError, setJoinRequestError] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<
    Array<{ requestId: string; displayName: string; requestedAt: string }>
  >([]);
  const [joinRequestBusyId, setJoinRequestBusyId] = useState<string | null>(null);
  const [accessState, setAccessState] = useState<SessionAccessState>({
    checking: true,
    exists: false,
    title: "",
    requiresCode: false,
    requiresApproval: false,
    isPrivate: false,
    createdAt: null,
    expiresAt: null,
    serverNow: null,
    error: null,
  });
  const [sessionUsers, setSessionUsers] = useState<SessionUserSummary[]>([]);
  const [sessionUsersBusyKey, setSessionUsersBusyKey] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<{ kind: "ok" | "warn" | "error"; message: string } | null>(null);

  const {
    tracks,
    drafts,
    permissions,
    participantToken,
    ownerToken,
    wsConnected,
    setSession,
    setError,
    setLoading,
    addTrack,
    removeTrack,
    setUsersOnline,
    setWsConnected,
    setPermissions,
    setParticipant,
    setShareCode,
    setExpiresAt,
    setServerTimeOffsetMs,
    setSessionExpired,
  } = useSessionStore();
  const { isPlaying, setPlayheadMs, setPlaying } = usePlaybackStore();
  const { trackParams, masterGain, removeTrack: removeMixerTrack } = useMixerStore();

  const totalDurationMs = Math.max(
    1000,
    ...tracks.map((track) => (track.offsetMs || 0) + (track.durationMs || 0)),
    ...drafts.map((draft) => (draft.offsetMs || 0) + (draft.durationMs || 0))
  );

  useEffect(() => {
    const context = new (window.AudioContext || (window as any).webkitAudioContext)();
    const engine = new AudioEngine(context);
    setAudioEngine(engine);

    return () => {
      engine.stop();
      setAudioEngine(null);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (noticeTimeoutRef.current !== null) {
        window.clearTimeout(noticeTimeoutRef.current);
        noticeTimeoutRef.current = null;
      }
      void context.close();
    };
  }, []);

  useEffect(() => {
    apiClient.setSessionToken(participantToken);
  }, [participantToken]);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    setLoading(true);
    setHasJoined(false);
    setWsConnected(false);
    apiClient.setSessionToken(null);
    loadedTrackIdsRef.current.clear();
    setTrackAudioErrors({});
    setSessionUsers([]);
    setSessionUsersBusyKey(null);
    setSessionNotice(null);

    const loadAccess = async () => {
      try {
        const access = await apiClient.getSessionAccess(sessionId);
        if (cancelled) return;

        if (!access.exists) {
          setAccessState({
            checking: false,
            exists: false,
            title: "",
            requiresCode: false,
            requiresApproval: false,
            isPrivate: false,
            createdAt: null,
            expiresAt: null,
            serverNow: null,
            error: "Session not found or expired.",
          });
          return;
        }

        const title = access.title || "Untitled Session";
        const requiresCode = Boolean(access.requiresCode);
        const isPrivate = Boolean(access.isPrivate);
        const requiresApproval = Boolean(access.requiresApproval);
        const createdAt = access.createdAt || null;
        const expiresAt = access.expiresAt || null;
        const serverNow = (access as any).serverNow || null;

        if (serverNow) {
          const offset = Date.parse(serverNow) - Date.now();
          if (Number.isFinite(offset)) {
            setServerTimeOffsetMs(offset);
          }
        }

        setSession(sessionId, title, { isPrivate, createdAt, expiresAt });
        setAccessState({
          checking: false,
          exists: true,
          title,
          requiresCode,
          requiresApproval,
          isPrivate,
          createdAt,
          expiresAt,
          serverNow,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : "Failed to check session access";
        setAccessState({
          checking: false,
          exists: false,
          title: "",
          requiresCode: false,
          requiresApproval: false,
          isPrivate: false,
          createdAt: null,
          expiresAt: null,
          serverNow: null,
          error: message,
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [sessionId, setLoading, setSession, setWsConnected, setServerTimeOffsetMs]);

  useEffect(() => {
    const needsAccessLogin = Boolean(accessState.error) && accessState.error?.toLowerCase().includes("sign in required");
    if (!needsAccessLogin) return;
    if (typeof window === "undefined") return;
    const key = "jamroom_access_redirected";
    if (sessionStorage.getItem(key) === "1") return;
    sessionStorage.setItem(key, "1");
    window.location.assign(buildAccessLoginUrl(window.location.href));
  }, [accessState.error]);

  const normalizeGuestKey = (name: string): string => {
    return String(name || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  };

  const pushNotice = (kind: "ok" | "warn" | "error", message: string, ttlMs = 4500) => {
    setSessionNotice({ kind, message });
    if (noticeTimeoutRef.current !== null) {
      window.clearTimeout(noticeTimeoutRef.current);
      noticeTimeoutRef.current = null;
    }
    if (kind !== "error" && ttlMs > 0) {
      noticeTimeoutRef.current = window.setTimeout(() => {
        noticeTimeoutRef.current = null;
        setSessionNotice(null);
      }, ttlMs);
    }
  };

  const handleJoin = async () => {
    if (!sessionId) return;

    const trimmedName = joinName.trim();
    if (!trimmedName) {
      alert("Please enter your name.");
      return;
    }

    if (accessState.requiresCode && !joinCode.trim()) {
      alert("Please enter the session code.");
      return;
    }

    try {
      setJoinLoading(true);
      setError(null);
      setJoinRequestError(null);
      setJoinRequestStatus(null);

      const applyJoinResponse = (joined: any) => {
        setSessionExpired(false);
        setParticipant({ token: joined.participantToken, name: joined.participantName });
        setPermissions(joined.permissions);

        const serverNow = joined.serverNow || null;
        if (serverNow) {
          const offset = Date.parse(serverNow) - Date.now();
          if (Number.isFinite(offset)) {
            setServerTimeOffsetMs(offset);
          }
        }

        setSession(sessionId, joined.title, {
          isPrivate: joined.isPrivate,
          createdAt: joined.createdAt || null,
          expiresAt: joined.expiresAt,
        });
        setShareCode(joined.shareCode || null);
        setExpiresAt(joined.expiresAt || null);

        loadedTrackIdsRef.current.clear();
        setTrackAudioErrors({});
        for (const track of joined.tracks || []) {
          addTrack(track);
        }

        setUsersOnline(joined.usersOnline || 1);
        setHasJoined(true);
      };

      // If approval is not required (eg demo sessions), join immediately.
      if (!accessState.requiresApproval) {
        const joined = await apiClient.joinSession(sessionId, {
          displayName: trimmedName,
          joinCode: accessState.requiresCode ? joinCode.trim() : undefined,
          ownerToken: ownerToken || undefined,
        });
        applyJoinResponse(joined);
        return;
      }

      // If this browser has the owner token, try joining as owner (no approval needed).
      if (ownerToken) {
        try {
          const joined = await apiClient.joinSession(sessionId, {
            displayName: trimmedName,
            joinCode: accessState.requiresCode ? joinCode.trim() : undefined,
            ownerToken,
          });
          applyJoinResponse(joined);
          return;
        } catch {
          // Fall back to join request flow.
        }
      }

      // Try a direct join first. The server will allow previously approved guests to re-enter by name.
      try {
        const joined = await apiClient.joinSession(sessionId, {
          displayName: trimmedName,
          joinCode: accessState.requiresCode ? joinCode.trim() : undefined,
        });
        applyJoinResponse(joined);
        return;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        const lower = msg.toLowerCase();
        if (lower.includes("removed from this session") || lower.includes("you have been removed")) {
          setJoinRequestStatus("DENIED");
          setJoinRequestError("You were removed from this session.");
          return;
        }
        if (!lower.includes("approval required")) {
          throw e;
        }
      }

      const created = await apiClient.createJoinRequest(sessionId, {
        displayName: trimmedName,
        joinCode: accessState.requiresCode ? joinCode.trim() : undefined,
      });

      setJoinRequestId(created.requestId);
      setJoinRequestStatus("PENDING");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to join session";
      setError(message);
      alert(message);
    } finally {
      setJoinLoading(false);
    }
  };

  const actOnJoinRequest = async (requestId: string, action: "approve" | "deny") => {
    if (!sessionId) return;
    try {
      setJoinRequestBusyId(requestId);
      if (action === "approve") {
        await apiClient.approveJoinRequest(sessionId, requestId);
      } else {
        await apiClient.denyJoinRequest(sessionId, requestId);
      }
      setPendingRequests((prev) => prev.filter((r) => r.requestId !== requestId));
    } catch (e) {
      alert(e instanceof Error ? e.message : "Join request action failed");
    } finally {
      setJoinRequestBusyId(null);
    }
  };

  const actOnSessionUser = async (displayName: string, action: "approve" | "remove" | "reapprove" | "add") => {
    if (!sessionId) return;
    const busyKey = normalizeGuestKey(displayName);
    try {
      setSessionUsersBusyKey(busyKey);
      const res = await apiClient.sessionUserAction(sessionId, action, displayName);
      if (res.user) {
        const user = res.user;
        setSessionUsers((prev) => {
          const idx = prev.findIndex((u) => u.guestNameNormalized === user.guestNameNormalized);
          if (idx === -1) return [user, ...prev];
          const next = [...prev];
          next[idx] = user;
          return next;
        });
      } else {
        const refreshed = await apiClient.listSessionUsers(sessionId);
        setSessionUsers(refreshed.users || []);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "User action failed");
    } finally {
      setSessionUsersBusyKey(null);
    }
  };

  const extendSession = async (hoursToAdd: number) => {
    if (!sessionId) return;
    try {
      const res = await apiClient.extendSession(sessionId, hoursToAdd);
      setExpiresAt(res.expiresAt || null);
      setSessionExpired(false);
      if (res.serverNow) {
        const offset = Date.parse(res.serverNow) - Date.now();
        if (Number.isFinite(offset)) {
          setServerTimeOffsetMs(offset);
        }
      }
      pushNotice("ok", `Session extended +${hoursToAdd}h`);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to extend session");
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    if (!joinRequestId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiClient.pollJoinRequest(sessionId, joinRequestId);
        if (cancelled) return;
        if (res.status === "PENDING") {
          setJoinRequestStatus("PENDING");
          return;
        }
        if (res.status === "DENIED") {
          setJoinRequestStatus("DENIED");
          setJoinRequestError(res.error || "Request denied");
          setJoinRequestId(null);
          return;
        }
        if (res.status === "APPROVED" && res.join) {
          setJoinRequestStatus(null);
          setJoinRequestError(null);
          setJoinRequestId(null);
          // Reuse existing join apply logic by calling join handler with the server-provided token.
          const joined = res.join as any;
          setSessionExpired(false);

          const serverNow = joined.serverNow || null;
          if (serverNow) {
            const offset = Date.parse(serverNow) - Date.now();
            if (Number.isFinite(offset)) {
              setServerTimeOffsetMs(offset);
            }
          }

          setParticipant({ token: joined.participantToken, name: joined.participantName });
          setPermissions(joined.permissions);
          setSession(sessionId, joined.title, {
            isPrivate: joined.isPrivate,
            createdAt: joined.createdAt || null,
            expiresAt: joined.expiresAt,
          });
          setShareCode(joined.shareCode || null);
          setExpiresAt(joined.expiresAt || null);
          loadedTrackIdsRef.current.clear();
          setTrackAudioErrors({});
          for (const track of joined.tracks || []) {
            addTrack(track);
          }
          setUsersOnline(joined.usersOnline || 1);
          setHasJoined(true);
        }
      } catch (e) {
        if (cancelled) return;
        setJoinRequestError(e instanceof Error ? e.message : "Failed to check join request");
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [joinRequestId, sessionId, addTrack, setExpiresAt, setParticipant, setPermissions, setSession, setShareCode, setUsersOnline, setServerTimeOffsetMs, setSessionExpired]);

  useEffect(() => {
    if (!sessionId) return;
    if (!hasJoined) return;
    if (!permissions.isOwner) return;
    apiClient
      .listJoinRequests(sessionId)
      .then((res) => {
        setPendingRequests(res.requests || []);
      })
      .catch(() => {
        // ignore
      });
  }, [hasJoined, permissions.isOwner, sessionId]);

  // Fallback polling: if WebSocket can't connect (blocked network / Access misconfig), still show requests.
  useEffect(() => {
    if (!sessionId) return;
    if (!hasJoined) return;
    if (!permissions.isOwner) return;
    if (wsConnected) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiClient.listJoinRequests(sessionId);
        if (cancelled) return;
        setPendingRequests(res.requests || []);
      } catch {
        // ignore
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 2500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [hasJoined, permissions.isOwner, sessionId, wsConnected]);

  useEffect(() => {
    if (!sessionId) return;
    if (!hasJoined) return;
    if (!permissions.isOwner) return;

    apiClient
      .listSessionUsers(sessionId)
      .then((res) => {
        setSessionUsers(res.users || []);
      })
      .catch(() => {
        // ignore
      });
  }, [hasJoined, permissions.isOwner, sessionId]);

  // Fallback polling for session users if WebSocket can't connect.
  useEffect(() => {
    if (!sessionId) return;
    if (!hasJoined) return;
    if (!permissions.isOwner) return;
    if (wsConnected) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiClient.listSessionUsers(sessionId);
        if (cancelled) return;
        setSessionUsers(res.users || []);
      } catch {
        // ignore
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 2500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [hasJoined, permissions.isOwner, sessionId, wsConnected]);

  const { ws } = useSessionSocket({
    sessionId: hasJoined ? sessionId || "" : "",
    token: participantToken || undefined,
    onOpen: () => {
      setWsConnected(true);
    },
    onClose: () => {
      setWsConnected(false);
    },
    onError: () => {
      setWsConnected(false);
    },
    onTrackPublished: (track) => {
      addTrack(track);
    },
    onTrackDeleted: (trackId) => {
      removeTrack(trackId);
      removeMixerTrack(trackId);
    },
    onUsersOnline: (count) => {
      setUsersOnline(count);
    },
    onJoinRequest: (event) => {
      setPendingRequests((prev) => {
        if (prev.some((r) => r.requestId === event.requestId)) return prev;
        return [{ requestId: event.requestId, displayName: event.displayName, requestedAt: event.requestedAt }, ...prev];
      });
    },
    onJoinRequestResolved: (event) => {
      setPendingRequests((prev) => prev.filter((r) => r.requestId !== event.requestId));
    },
    onSessionExtended: (event) => {
      setSessionExpired(false);
      setExpiresAt(event.expiresAt || null);
      if (event.serverNow) {
        const offset = Date.parse(event.serverNow) - Date.now();
        if (Number.isFinite(offset)) {
          setServerTimeOffsetMs(offset);
        }
      }
      pushNotice("ok", `Session extended +${event.hoursAdded}h`);
    },
    onSessionExpired: (event) => {
      setSessionExpired(true);
      setExpiresAt(event.expiresAt || null);
      if (event.serverNow) {
        const offset = Date.parse(event.serverNow) - Date.now();
        if (Number.isFinite(offset)) {
          setServerTimeOffsetMs(offset);
        }
      }
      pushNotice("error", "Session expired", 0);
      if (audioEngine) {
        audioEngine.stop();
        setPlaying(false);
        setPlayheadMs(0);
      }
    },
    onSessionUsersSnapshot: (event) => {
      setSessionUsers(event.users || []);
    },
    onSessionUserUpdate: (event) => {
      const user = event.user;
      if (!user) return;
      setSessionUsers((prev) => {
        const idx = prev.findIndex((u) => u.guestNameNormalized === user.guestNameNormalized);
        if (idx === -1) return [user, ...prev];
        const next = [...prev];
        next[idx] = user;
        return next;
      });
    },
  });

  useEffect(() => {
    if (!audioEngine || !hasJoined) return;

    const canPlayMimeType = (mimeType: string): boolean => {
      if (typeof document === "undefined") return true;
      try {
        const audio = document.createElement("audio");
        const res = audio.canPlayType(mimeType);
        return res === "probably" || res === "maybe";
      } catch {
        return true;
      }
    };

    const canPlayWebmOpus = (): boolean => {
      return (
        canPlayMimeType('audio/webm; codecs="opus"') ||
        canPlayMimeType("audio/webm;codecs=opus")
      );
    };

    tracks.forEach((track) => {
      if (!track.objectKey || loadedTrackIdsRef.current.has(track.id)) return;

      // iOS/WebKit browsers cannot decode WebM/Opus. Surface a clear error instead of failing silently.
      if (track.format === "webm_opus" && !canPlayWebmOpus()) {
        loadedTrackIdsRef.current.add(track.id);
        setTrackAudioErrors((prev) => ({
          ...prev,
          [track.id]:
            'This device cannot play WebM/Opus audio. Re-upload/publish this track as WAV or MP3 to support iPhone/iPad.',
        }));
        return;
      }

      loadedTrackIdsRef.current.add(track.id);
      setTrackAudioErrors((prev) => {
        if (!prev[track.id]) return prev;
        const next = { ...prev };
        delete next[track.id];
        return next;
      });
      apiClient
        .fetchTrackAudio(track.sessionId || (sessionId || ""), track.id)
        .then((blob) => audioEngine.loadBuffer(track.id, blob))
        .catch((err) => {
          loadedTrackIdsRef.current.delete(track.id);
          console.error("Failed to load track:", err);
          const message = err instanceof Error ? err.message : String(err);
          setTrackAudioErrors((prev) => ({
            ...prev,
            [track.id]:
              track.format === "webm_opus"
                ? 'This track is in WebM/Opus and failed to decode on this device. Re-upload as WAV or MP3.'
                : message,
          }));
        });
    });
  }, [audioEngine, tracks, hasJoined, sessionId]);

  useEffect(() => {
    if (!audioEngine) return;

    const existingIds = new Set<string>([
      ...tracks.map((track) => track.id),
      ...drafts.map((draft) => draft.localId),
    ]);

    audioEngine.getLoadedTrackIds().forEach((loadedId) => {
      if (!existingIds.has(loadedId)) {
        audioEngine.removeTrack(loadedId);
        loadedTrackIdsRef.current.delete(loadedId);
      }
    });
  }, [audioEngine, tracks, drafts]);

  useEffect(() => {
    if (!audioEngine) return;

    const trackOffsetById = new Map<string, number>();
    tracks.forEach((track) => {
      trackOffsetById.set(track.id, track.offsetMs || 0);
    });
    drafts.forEach((draft) => {
      trackOffsetById.set(draft.localId, draft.offsetMs || 0);
    });

    const ids = Array.from(trackOffsetById.keys());

    const hasSolo = ids.some((id) => (trackParams.get(id)?.solo ?? false));
    audioEngine.setMasterGain(masterGain);

    ids.forEach((id) => {
      const params = trackParams.get(id) || {
        mute: false,
        solo: false,
        gain: 1,
        nudgeMs: 0,
      };

      const timingOffset = (trackOffsetById.get(id) || 0) + (params.nudgeMs || 0);
      audioEngine.setTrackOffset(id, timingOffset);

      const isAudible = !params.mute && (!hasSolo || params.solo);
      const effectiveGain = isAudible ? params.gain : 0;
      audioEngine.setTrackGain(id, effectiveGain);
    });
  }, [audioEngine, tracks, drafts, trackParams, masterGain]);

  useEffect(() => {
    if (!audioEngine || !isPlaying) return;

    const tick = () => {
      if (!audioEngine.isPlaying) {
        setPlaying(false);
        return;
      }

      setPlayheadMs(audioEngine.getPlayheadMs());
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [audioEngine, isPlaying, setPlayheadMs, setPlaying]);

  if (!sessionId) {
    return <div>Invalid session</div>;
  }

  if (!hasJoined) {
    const needsAccessLogin = Boolean(accessState.error) && accessState.error?.toLowerCase().includes("sign in required");

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "radial-gradient(circle at top, #283247 0%, #151922 48%, #0f1118 100%)",
          color: "#e2e8f3",
          padding: 24,
        }}
      >
        <div
          style={{
            width: "min(560px, 100%)",
            borderRadius: 14,
            border: "1px solid #3a4660",
            background: "#1c2331",
            padding: 24,
            boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <img
              src="/branding/jamroom-mark-180.png"
              alt=""
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 14,
                boxShadow: "0 14px 36px rgba(0, 212, 255, 0.12), 0 14px 36px rgba(255, 43, 214, 0.1)",
              }}
            />
            <img
              src="/branding/jamroom-wordmark.png"
              alt="JamRoom"
              style={{
                height: 26,
                width: "auto",
                filter: "drop-shadow(0 18px 48px rgba(0, 212, 255, 0.14))",
              }}
            />
          </div>
          <h2 style={{ margin: "0 0 8px" }}>{accessState.title || "Join Session"}</h2>
          {accessState.expiresAt && (
            <p style={{ margin: "0 0 16px", color: "#b9c5db", fontSize: 13 }}>
              Expires: {new Date(accessState.expiresAt).toLocaleString()}
            </p>
          )}

          {accessState.checking ? (
            <p>Checking session...</p>
          ) : accessState.error ? (
            <div style={{ display: "grid", gap: 10 }}>
              <p style={{ color: "#ffb3b3", margin: 0 }}>
                {needsAccessLogin ? "Sign in required to join this session." : accessState.error || "Unable to reach the API."}
              </p>
              {needsAccessLogin && (
                <div style={{ padding: 12, border: "1px solid #5a4721", borderRadius: 10, background: "#2a2417" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Cloudflare Access login required</div>
                  <div style={{ color: "#d9c69c", fontSize: 13, marginBottom: 10 }}>
                    If you are not redirected automatically, click below to sign in.
                  </div>
                  <button
                    type="button"
                    onClick={() => window.location.assign(buildAccessLoginUrl(window.location.href))}
                    className="jr-btn jr-btn--primary"
                  >
                    Sign in
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 13, color: "#c6d2e7" }}>Your name</span>
                  <input
                    type="text"
                    value={joinName}
                    onChange={(e) => setJoinName(e.target.value)}
                    placeholder="Ej: Jose"
                    disabled={joinRequestStatus === "PENDING"}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      border: "1px solid #495874",
                      background: "#121722",
                      color: "#e2e8f3",
                      padding: "10px 12px",
                    }}
                  />
                </label>

                {accessState.requiresCode && (
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontSize: 13, color: "#c6d2e7" }}>Private session code</span>
                    <input
                      type="text"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value)}
                      placeholder="Enter code"
                      disabled={joinRequestStatus === "PENDING"}
                      style={{
                        width: "100%",
                        borderRadius: 8,
                        border: "1px solid #495874",
                        background: "#121722",
                        color: "#e2e8f3",
                        padding: "10px 12px",
                      }}
                    />
                  </label>
                )}
              </div>

              {(joinRequestStatus === "PENDING" || joinRequestStatus === "DENIED" || joinRequestError) && (
                <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                  {joinRequestStatus === "PENDING" && (
                    <div style={{ padding: 12, border: "1px solid #3f4a5c", borderRadius: 10, background: "#121722" }}>
                      Waiting for the owner to approve your request...
                    </div>
                  )}
                  {joinRequestStatus === "DENIED" && (
                    <div style={{ padding: 12, border: "1px solid #7a2e2e", borderRadius: 10, background: "#3a1f1f", color: "#ffb3b3" }}>
                      Request denied{joinRequestError ? `: ${joinRequestError}` : "."}
                    </div>
                  )}
                  {joinRequestError && joinRequestStatus !== "DENIED" && (
                    <div style={{ color: "#ffb3b3" }}>{joinRequestError}</div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={handleJoin}
                disabled={joinLoading || joinRequestStatus === "PENDING" || !accessState.exists}
                className="jr-btn jr-btn--primary"
                style={{ marginTop: 18 }}
              >
                {joinLoading
                  ? "Joining..."
                  : joinRequestStatus === "PENDING"
                    ? "Waiting for approval..."
                    : accessState.requiresApproval
                      ? "Request to Join"
                      : "Join Session"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#1a1a1a" }}>
      <SessionTopBar />
      {sessionNotice && (
        <div
          style={{
            margin: "10px 16px 0",
            padding: 12,
            borderRadius: 12,
            border:
              sessionNotice.kind === "error"
                ? "1px solid #7a2e2e"
                : sessionNotice.kind === "warn"
                  ? "1px solid #5a4721"
                  : "1px solid rgba(52, 245, 165, 0.22)",
            background:
              sessionNotice.kind === "error"
                ? "#3a1f1f"
                : sessionNotice.kind === "warn"
                  ? "#2a2417"
                  : "rgba(52, 245, 165, 0.08)",
            color:
              sessionNotice.kind === "error"
                ? "#ffb3b3"
                : sessionNotice.kind === "warn"
                  ? "#d9c69c"
                  : "rgba(164, 255, 219, 0.95)",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {sessionNotice.message}
        </div>
      )}
      {Object.keys(trackAudioErrors).length > 0 && (
        <div
          style={{
            margin: "10px 16px 0",
            padding: 12,
            borderRadius: 12,
            border: "1px solid #7a2e2e",
            background: "#3a1f1f",
            color: "#ffb3b3",
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Audio warning</div>
          {Object.entries(trackAudioErrors)
            .slice(0, 4)
            .map(([trackId, message]) => {
              const label = tracks.find((t) => t.id === trackId)?.displayName || trackId;
              return (
                <div key={trackId} style={{ marginTop: 4 }}>
                  <span style={{ fontWeight: 700 }}>{label}:</span> {message}
                </div>
              );
            })}
          {Object.keys(trackAudioErrors).length > 4 && (
            <div style={{ marginTop: 6, color: "#d9c69c" }}>
              More tracks failed to load.
            </div>
          )}
        </div>
      )}
      <TransportBar audioEngine={audioEngine} totalDurationMs={totalDurationMs} />
      <TimelineRuler />
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <TracksPanel />
      </div>
      <RecorderDock
        sessionId={sessionId}
        audioEngine={audioEngine}
        ws={ws}
        rightControls={
          permissions.isOwner ? (
            <SessionUsersWidget
              placement="dock"
              users={sessionUsers}
              busyKey={sessionUsersBusyKey}
              onAction={actOnSessionUser}
              onExtendSession={extendSession}
            />
          ) : null
        }
      />
      {permissions.isOwner && (
        <JoinRequestsWidget requests={pendingRequests} busyId={joinRequestBusyId} onAction={actOnJoinRequest} />
      )}
    </div>
  );
};

export default SessionPage;
